import { useState } from 'react';
import { AlertTriangle, Check, Loader2, Plus, Sparkles, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input, Label, Select, Switch, Textarea } from '@/components/ui/Input';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import {
  createEmptyMailKnowledgeDoc,
  MAIL_ASSISTANT_AUTO_REPLY_OPTIONS,
  MAIL_KNOWLEDGE_KIND_OPTIONS,
  MAIL_KNOWLEDGE_SCOPE_OPTIONS,
  mergeMailAssistantDocs,
} from '../lib/mail-assistant';
import {
  useWizardStore,
  type MailAssistantPolicyDraft,
  type MailKnowledgeDocDraft,
  type MailKnowledgeDocKind,
} from '../lib/wizard-store';

const KNOWLEDGE_ERROR_CODES = {
  ai_not_configured: "L'IA n'est pas configurée pour cet environnement.",
  llm_upstream_failed: "L'assistant IA n'a pas répondu correctement. Réessaie dans quelques secondes.",
  llm_bad_format: "La suggestion IA a renvoyé un format inattendu.",
  ERR_VALIDATION: "Le contexte campagne est trop incomplet pour générer une knowledge base utile.",
};

function Hint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-gray-400">{children}</p>;
}

interface SuggestMailKnowledgeResponse {
  policy: MailAssistantPolicyDraft & { notes?: string[] };
  documents: Array<Omit<MailKnowledgeDocDraft, 'id'>>;
  model?: string;
  websiteUsed?: string | null;
}

export function W6_MailAssistantKnowledge() {
  const store = useWizardStore();
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [assistantError, setAssistantError] = useState<string | null>(null);
  const [assistantMessage, setAssistantMessage] = useState<string | null>(null);
  const [assistantNotes, setAssistantNotes] = useState<string[]>([]);

  const {
    sender_company_name,
    sender_description,
    sender_website,
    offer_title,
    offer_description,
    offer_url,
    target_country,
    target_region,
    target_city,
    language,
    email_templates,
    fit_gate,
    mail_assistant_policy,
    mail_assistant_docs,
    patchMailAssistantPolicy,
    setMailAssistantDocs,
    addMailAssistantDoc,
    updateMailAssistantDoc,
    removeMailAssistantDoc,
  } = store;

  async function runAssistant() {
    setLoadingSuggestion(true);
    setAssistantError(null);
    setAssistantMessage(null);
    setAssistantNotes([]);

    try {
      const { data } = await apiClient.post<SuggestMailKnowledgeResponse>(
        '/v1/llm/suggest-mail-knowledge',
        {
          senderCompanyName: sender_company_name || null,
          senderDescription: sender_description || null,
          senderWebsite: sender_website || null,
          offerTitle: offer_title || null,
          offerDescription: offer_description || null,
          offerUrl: offer_url || null,
          targetCountry: target_country || null,
          targetRegion: target_region || null,
          targetCity: target_city || null,
          language,
          emailTemplates: {
            email01: email_templates.email01,
            email02: email_templates.email02,
            email03: email_templates.email03,
          },
          fitGate: fit_gate,
          currentPolicy: mail_assistant_policy,
          currentDocs: mail_assistant_docs,
        },
      );

      patchMailAssistantPolicy({
        scope: data.policy.scope,
        auto_reply_enabled: data.policy.auto_reply_enabled,
        auto_reply_labels: data.policy.auto_reply_labels,
        tone: data.policy.tone,
        escalation_notes: data.policy.escalation_notes,
        forbidden_claims: data.policy.forbidden_claims,
        meeting_booking_instructions: data.policy.meeting_booking_instructions,
        signature_style: data.policy.signature_style,
      });
      setMailAssistantDocs(mergeMailAssistantDocs(mail_assistant_docs, data.documents));
      setAssistantNotes(data.policy.notes ?? []);
      setAssistantMessage(
        data.websiteUsed
          ? 'Base de knowledge et policy pré-remplies à partir du contexte campagne et du site expéditeur.'
          : 'Base de knowledge et policy pré-remplies à partir du contexte campagne.',
      );
    } catch (error) {
      setAssistantError(getApiErrorMessage(error, KNOWLEDGE_ERROR_CODES));
    } finally {
      setLoadingSuggestion(false);
    }
  }

  function toggleAutoReplyLabel(label: MailAssistantPolicyDraft['auto_reply_labels'][number]) {
    const current = new Set(mail_assistant_policy.auto_reply_labels);
    if (current.has(label)) {
      current.delete(label);
    } else {
      current.add(label);
    }
    patchMailAssistantPolicy({
      auto_reply_labels: [...current],
    });
  }

  function addDoc(kind: MailKnowledgeDocKind) {
    addMailAssistantDoc(createEmptyMailKnowledgeDoc(kind));
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="AI knowledge & policy"
          subtitle="Prépare la base de connaissance qui aide l'inbox IA à qualifier les réponses, suggérer un draft, et savoir quand escalader."
        />
        <CardContent className="space-y-5" data-testid="mail-assistant-knowledge-section">
          <div
            className="rounded-md border border-dashed border-brand-primary/40 bg-brand-primary/5 p-4 space-y-3"
            data-testid="mail-knowledge-ai-assistant"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm text-gray-700">
                <p className="font-medium text-brand-dark">Assistant IA de knowledge base</p>
                <p className="mt-1 text-xs text-gray-500">
                  Propose une policy de réponse prudente, quelques FAQ utiles, des objections,
                  et des exemples de réponse ancrés dans le contexte de la campagne.
                </p>
                {assistantError && (
                  <span className="mt-2 flex items-start gap-1 text-xs text-red-600">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                    {assistantError}
                  </span>
                )}
                {assistantMessage && (
                  <span className="mt-2 flex items-start gap-1 text-xs text-emerald-700">
                    <Check size={12} className="mt-0.5 shrink-0" />
                    {assistantMessage}
                  </span>
                )}
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={runAssistant}
                disabled={loadingSuggestion}
                data-testid="mail-knowledge-ai-button"
              >
                {loadingSuggestion ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Analyse…
                  </>
                ) : (
                  <>
                    <Sparkles size={14} />
                    Pré-remplir avec l'IA
                  </>
                )}
              </Button>
            </div>

            {assistantNotes.length > 0 && (
              <ul className="space-y-1 text-xs text-gray-600" data-testid="mail-knowledge-ai-notes">
                {assistantNotes.map((note) => (
                  <li key={note}>• {note}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="space-y-4 border border-gray-200 p-4" data-testid="assistant-policy-card">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-brand-dark">Reply policy</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Définit le ton, les garde-fous et ce que l'assistant peut auto-répondre sans validation humaine.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Auto-reply</span>
                  <Switch
                    checked={mail_assistant_policy.auto_reply_enabled}
                    onChange={(value) => patchMailAssistantPolicy({ auto_reply_enabled: value })}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="assistant-policy-scope">Policy scope</Label>
                <Select
                  id="assistant-policy-scope"
                  value={mail_assistant_policy.scope}
                  onChange={(event) =>
                    patchMailAssistantPolicy({
                      scope: event.target.value as MailAssistantPolicyDraft['scope'],
                    })
                  }
                  data-testid="assistant-policy-scope"
                >
                  {MAIL_KNOWLEDGE_SCOPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-2" data-testid="assistant-auto-reply-labels">
                <Label>Safe auto-reply labels</Label>
                {MAIL_ASSISTANT_AUTO_REPLY_OPTIONS.map((option) => (
                  <Checkbox
                    key={option.value}
                    id={`assistant-label-${option.value}`}
                    checked={mail_assistant_policy.auto_reply_labels.includes(option.value)}
                    onChange={() => toggleAutoReplyLabel(option.value)}
                    label={option.label}
                    description={option.description}
                    data-testid={`assistant-label-${option.value}`}
                  />
                ))}
              </div>

              <div className="space-y-1">
                <Label htmlFor="assistant-tone">Tone</Label>
                <Input
                  id="assistant-tone"
                  value={mail_assistant_policy.tone}
                  onChange={(event) => patchMailAssistantPolicy({ tone: event.target.value })}
                  placeholder="Sobre, direct, professionnel et chaleureux"
                  data-testid="assistant-tone"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="assistant-escalation">Escalation notes</Label>
                <Textarea
                  id="assistant-escalation"
                  rows={4}
                  value={mail_assistant_policy.escalation_notes}
                  onChange={(event) =>
                    patchMailAssistantPolicy({ escalation_notes: event.target.value })
                  }
                  placeholder="Quand basculer la réponse vers un humain ?"
                  data-testid="assistant-escalation"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="assistant-forbidden-claims">Forbidden claims</Label>
                <Textarea
                  id="assistant-forbidden-claims"
                  rows={4}
                  value={mail_assistant_policy.forbidden_claims}
                  onChange={(event) =>
                    patchMailAssistantPolicy({ forbidden_claims: event.target.value })
                  }
                  placeholder="Ce que l'IA n'a jamais le droit d'inventer."
                  data-testid="assistant-forbidden-claims"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="assistant-meeting-booking">Meeting booking instructions</Label>
                <Textarea
                  id="assistant-meeting-booking"
                  rows={3}
                  value={mail_assistant_policy.meeting_booking_instructions}
                  onChange={(event) =>
                    patchMailAssistantPolicy({
                      meeting_booking_instructions: event.target.value,
                    })
                  }
                  placeholder="Quand le lead veut un rendez-vous, que doit faire l'assistant ?"
                  data-testid="assistant-meeting-booking"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="assistant-signature-style">Signature style</Label>
                <Textarea
                  id="assistant-signature-style"
                  rows={3}
                  value={mail_assistant_policy.signature_style}
                  onChange={(event) =>
                    patchMailAssistantPolicy({ signature_style: event.target.value })
                  }
                  placeholder="Comment terminer les réponses."
                  data-testid="assistant-signature-style"
                />
              </div>
            </div>

            <div className="space-y-4 border border-gray-200 p-4" data-testid="assistant-docs-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-brand-dark">Knowledge docs</p>
                  <p className="mt-1 text-xs text-gray-500">
                    FAQ, objections, notes produit et exemples de réponse qui nourrissent la qualification et les drafts.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => addDoc('faq')}
                    data-testid="add-knowledge-faq"
                  >
                    <Plus size={12} />
                    FAQ
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => addDoc('objection')}
                    data-testid="add-knowledge-objection"
                  >
                    <Plus size={12} />
                    Objection
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => addDoc('example_reply')}
                    data-testid="add-knowledge-example-reply"
                  >
                    <Plus size={12} />
                    Example
                  </Button>
                </div>
              </div>

              {mail_assistant_docs.length === 0 && (
                <div className="rounded border border-dashed border-gray-200 px-4 py-6 text-sm text-gray-500">
                  Aucune doc pour le moment. Tu peux pré-remplir avec l'IA ou ajouter tes propres FAQs / objections validées.
                </div>
              )}

              <div className="space-y-4">
                {mail_assistant_docs.map((doc) => (
                  <div key={doc.id} className="rounded border border-gray-200 p-4" data-testid={`knowledge-doc-${doc.id}`}>
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="grid flex-1 gap-3 md:grid-cols-[160px_160px_minmax(0,1fr)]">
                        <Select
                          value={doc.kind}
                          onChange={(event) =>
                            updateMailAssistantDoc(doc.id, {
                              kind: event.target.value as MailKnowledgeDocKind,
                            })
                          }
                          data-testid={`knowledge-kind-${doc.id}`}
                        >
                          {MAIL_KNOWLEDGE_KIND_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                        <Select
                          value={doc.scope}
                          onChange={(event) =>
                            updateMailAssistantDoc(doc.id, {
                              scope: event.target.value as MailKnowledgeDocDraft['scope'],
                            })
                          }
                          data-testid={`knowledge-scope-${doc.id}`}
                        >
                          {MAIL_KNOWLEDGE_SCOPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                        <Input
                          value={doc.title}
                          onChange={(event) =>
                            updateMailAssistantDoc(doc.id, { title: event.target.value })
                          }
                          placeholder="Title"
                          data-testid={`knowledge-title-${doc.id}`}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMailAssistantDoc(doc.id)}
                        data-testid={`remove-knowledge-doc-${doc.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                    <Textarea
                      rows={6}
                      value={doc.content_md}
                      onChange={(event) =>
                        updateMailAssistantDoc(doc.id, { content_md: event.target.value })
                      }
                      placeholder="Approved guidance, objection handling, or example reply."
                      data-testid={`knowledge-content-${doc.id}`}
                    />
                  </div>
                ))}
              </div>

              <Hint>
                Astuce: garde les docs courts, approuvés, et formulés comme des règles ou réponses réutilisables.
              </Hint>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
